import React from 'react'
import type {
  CoberturaTrasladoItem,
  CoberturaTrasladoTransfer,
  DevolucionUnitLine,
  DevolucionUnitSelections,
  EstadoUnidadDevolucion
} from '../../types/devoluciones'
import {
  getCoberturaItemBySku,
  getUnitTrasladoInfo,
  type UnidadTrasladoUiStatus
} from '../../utils/devoluciones-utils'

interface DevolucionUnitsTableProps {
  units: DevolucionUnitLine[]
  selections: DevolucionUnitSelections
  onChange: (unitId: string, value: EstadoUnidadDevolucion) => void
  disabled?: boolean
  coberturaItems?: CoberturaTrasladoItem[]
  coberturaTransfers?: CoberturaTrasladoTransfer[]
}

function getStatusStyles(status: UnidadTrasladoUiStatus): {
  rowClassName: string
  textClassName: string
  label: string
} | null {
  if (status === 'aprobado') {
    return {
      rowClassName: 'bg-green-50 text-green-900',
      textClassName: 'text-green-900',
      label: 'Transferencia aprobada'
    }
  }
  if (status === 'pendiente') {
    return {
      rowClassName: 'bg-amber-50 text-amber-900',
      textClassName: 'text-amber-900',
      label: 'Transferencia pendiente'
    }
  }
  return null
}

const DevolucionUnitsTable: React.FC<DevolucionUnitsTableProps> = ({
  units,
  selections,
  onChange,
  disabled = false,
  coberturaItems = [],
  coberturaTransfers = []
}) => {
  if (!units.length) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center text-gray-500">
        Este pedido no tiene productos para verificar.
      </div>
    )
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-blue-800">Verificación de unidades</h3>
        <p className="mt-1 text-sm text-gray-500">
          Cada unidad se muestra en una fila independiente según la cantidad del pedido.
          Verde: transferencia aprobada. Amarillo: transferencia pendiente.
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-700">SKU</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Producto</th>
              <th className="px-3 py-2 text-center font-medium text-gray-700">Unidad</th>
              <th className="px-3 py-2 text-center font-medium text-gray-700">
                ¿Está en buen estado?
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {units.map((unit) => {
              const coberturaItem = getCoberturaItemBySku(coberturaItems, unit.sku)
              const trasladoInfo = getUnitTrasladoInfo(unit, coberturaItem, coberturaTransfers)
              const statusStyles = getStatusStyles(trasladoInfo.status)
              const isLocked = trasladoInfo.status != null
              const detailLabel = statusStyles
                ? trasladoInfo.consecutivo
                  ? `${statusStyles.label} (${trasladoInfo.consecutivo})`
                  : statusStyles.label
                : null

              return (
                <tr
                  key={unit.id}
                  className={statusStyles?.rowClassName}
                >
                  <td className={`px-3 py-2 font-medium ${statusStyles?.textClassName || 'text-gray-900'}`}>
                    {unit.sku}
                  </td>
                  <td className={`px-3 py-2 ${statusStyles?.textClassName || 'text-gray-700'}`}>
                    {unit.producto}
                    {detailLabel && (
                      <span className={`ml-2 text-xs font-medium ${statusStyles?.textClassName}`}>
                        ({detailLabel})
                      </span>
                    )}
                  </td>
                  <td className={`px-3 py-2 text-center ${statusStyles?.textClassName || 'text-gray-700'}`}>
                    {unit.unitIndex + 1}
                  </td>
                  <td className="px-3 py-2">
                    {isLocked ? (
                      <p className={`text-center text-xs font-medium ${statusStyles?.textClassName}`}>
                        {detailLabel}
                      </p>
                    ) : (
                      <fieldset
                        className="flex justify-center gap-4"
                        aria-label={`Estado de ${unit.sku} unidad ${unit.unitIndex + 1}`}
                      >
                        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="radio"
                            name={`estado-${unit.id}`}
                            value="buen_estado"
                            checked={selections[unit.id] === 'buen_estado'}
                            onChange={() => onChange(unit.id, 'buen_estado')}
                            disabled={disabled}
                            className="h-4 w-4 border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          Sí
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="radio"
                            name={`estado-${unit.id}`}
                            value="mal_estado"
                            checked={selections[unit.id] === 'mal_estado'}
                            onChange={() => onChange(unit.id, 'mal_estado')}
                            disabled={disabled}
                            className="h-4 w-4 border-gray-300 text-red-600 focus:ring-red-500"
                          />
                          No
                        </label>
                      </fieldset>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default DevolucionUnitsTable
